import { Menu, MenuItem, Box, Button, Typography } from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import AddIcon from "@mui/icons-material/Add";
import NotesIcon from "@mui/icons-material/Notes";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import {
  AddPresentationSectionActions,
  AddPresentationSectionOptions,
} from "../../usePresentationLogic";
import { useCallback, useState } from "react";

const Container = styled(Box)`
  width: 100%;
  position: relative;
  padding: 0;
`;

const AddSectionButtonContainer = styled.div<{ $isMenuOpen?: boolean }>`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  max-height: 1.5rem;
  min-height: 1.5rem;
  transition: all 0.2s;

  &::before {
    content: "";
    display: block;
    width: 100%;
    position: absolute;
    top: calc(100% - 0.75rem);
    left: 0;
    z-index: 10;
    border-top: 2px dashed ${(p) => p.theme.colors.border2};
    transition: border 0.2s;
  }

  button {
    position: relative;
    z-index: 20;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    &::before {
      border-top: 2px solid ${(p) => p.theme.colors.primary};
    }

    button {
      opacity: 1;
    }
  }

  ${(p) => p.theme.media.mobileDown} {
    opacity: 1;

    button {
      position: relative;
      z-index: 2;
      height: 2.25rem;
    }
  }
`;

const ActionItemText = styled(Typography)`
  font-size: ${(p) => p.theme.text.fontSizes.m};
`;

type Props = {
  addSectionActions: AddPresentationSectionActions;
  insertAfterIndex: number;
};

export const AddSectionButton = (props: Props) => {
  const { addSectionActions, insertAfterIndex } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleMenuItemClick = useCallback(
    (action: (options: AddPresentationSectionOptions) => void) => {
      action({ insertAfterIndex });
      handleClose();
    },
    [handleClose, insertAfterIndex],
  );

  return (
    <Container>
      <AddSectionButtonContainer $isMenuOpen={open}>
        <Button
          startIcon={<AddIcon />}
          size="small"
          onClick={handleClick}
          aria-controls={open ? "add-section-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          Add Section
        </Button>
        <Menu
          id="add-section-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          MenuListProps={{
            "aria-labelledby": "add-section-button",
          }}
        >
          {!!addSectionActions["ai-assisted-text"] && (
            <MenuItem
              onClick={() =>
                handleMenuItemClick(addSectionActions["ai-assisted-text"]!)
              }
            >
              <NotesIcon fontSize="small" />
              <ActionItemText fontWeight={700} variant="overline">
                Text
              </ActionItemText>
            </MenuItem>
          )}
          {!!addSectionActions["branding"] && (
            <MenuItem
              onClick={() =>
                handleMenuItemClick(addSectionActions["branding"]!)
              }
            >
              <PersonIcon fontSize="small" />
              <ActionItemText fontWeight={700} variant="overline">
                Contact Info
              </ActionItemText>
            </MenuItem>
          )}
          {!!addSectionActions["team-branding"] && (
            <MenuItem
              onClick={() =>
                handleMenuItemClick(addSectionActions["team-branding"]!)
              }
            >
              <GroupsIcon fontSize="small" />
              <ActionItemText fontWeight={700} variant="overline">
                Team Info
              </ActionItemText>
            </MenuItem>
          )}
        </Menu>
      </AddSectionButtonContainer>
    </Container>
  );
};
